import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin, Clock, Star } from "lucide-react";
import { locationService } from "@/services/LocationService";

interface Restaurant {
  id: string;
  name: string;
  address: string;
  city: string;
  category?: string;
  priceRange?: string;
}

interface LocationPromptProps {
  isOpen: boolean;
  restaurant: Restaurant;
  timeSpent: number;
  sessionId: string;
  onStartReview: (restaurantId: string, sessionId: string) => void;
  onDismiss: () => void;
}

export default function LocationPrompt({ 
  isOpen, 
  restaurant, 
  timeSpent, 
  sessionId,
  onStartReview, 
  onDismiss 
}: LocationPromptProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [kudozEarnable, setKudozEarnable] = useState(45); // Base amount for creating review

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Calculate Kudoz based on possible actions
      calculateKudozReward();
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const calculateKudozReward = () => {
    // Base rewards for review actions:
    // Rating: 5 Kudoz
    // Text review: 10 Kudoz  
    // Photo: 15 Kudoz
    // Video: 20 Kudoz
    // Sharing with friends: 5 Kudoz
    // Total possible: 55 Kudoz
    setKudozEarnable(55);
  };

  const handleStartReview = () => {
    onStartReview(restaurant.id, sessionId);
    setIsVisible(false);
  };

  const handleDismiss = () => {
    onDismiss();
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md mx-auto shadow-2xl border-2 border-primary/20">
        <CardHeader className="text-center space-y-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
            <MapPin className="w-8 h-8 text-primary" />
          </div>
          
          <div>
            <CardTitle className="text-xl mb-2">
              At {restaurant.name}?
            </CardTitle>
            <CardDescription className="text-sm">
              Share your experience and earn Kudoz!
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Restaurant Info */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <span>{restaurant.address}</span>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span>You've been here for {timeSpent} minutes</span>
            </div>

            {restaurant.category && (
              <div className="flex items-center gap-2 text-sm">
                <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                  {restaurant.category}
                </span>
                {restaurant.priceRange && (
                  <span className="px-2 py-1 bg-muted rounded-full text-xs">
                    {restaurant.priceRange}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Kudoz Incentive */}
          <div className="bg-gradient-to-r from-primary/10 to-purple-500/10 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Star className="w-5 h-5 text-yellow-500 fill-current" />
              <span className="font-bold text-lg">Earn up to {kudozEarnable} Kudoz!</span>
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              <div>✓ Rating: +5 Kudoz</div>
              <div>✓ Text review: +10 Kudoz</div>
              <div>✓ Photo: +15 Kudoz</div>
              <div>✓ Video: +20 Kudoz</div>
              <div>✓ Share with friends: +5 Kudoz</div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <Button 
              onClick={handleStartReview}
              className="w-full"
              data-testid="button-start-review"
            >
              Write Review & Earn Kudoz
            </Button>
            
            <Button 
              variant="ghost" 
              onClick={handleDismiss}
              className="w-full"
              data-testid="button-dismiss-prompt"
            >
              Maybe Later
            </Button>
          </div>

          {/* Privacy Note */}
          <div className="text-xs text-muted-foreground text-center">
            We detected your location to suggest nearby restaurants. 
            Your location data is not stored permanently.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}